import React, { useEffect, useState } from "react";
import { Modal, ModalDialog, Button, Select, Option } from "@mui/joy";
import { Autocomplete, TextField } from "@mui/material";
import { postFetch } from "../../../../../fetch/fetch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "../../booking/components/phoneinbound/phoneinbound.module.css";

const toastOptions : any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored"
};

//발신번호 "선택 안 함" 센티널 (Joy Select 는 null 값을 못 고르므로)
const NONE = "__none";

const errorText = (message : string) => {
    switch (message) {
        case "ALREADY_REGISTERED": return "이미 등록된 계정입니다";
        case "INVALID_AGENT": return "교환기에 없는 내선입니다. 다시 선택해주세요";
        case "INVALID_BRANCH": return "발신번호를 다시 선택해주세요";
        case "NOT_AUTHORIZED": return "권한이 없습니다";
        case "noData": return "선생님을 다시 선택해주세요";
        default: return "등록 실패: " + message;
    }
};

//유선폰 설정 모달: 선생님(필수) → 상담사 번호(필수) → 발신번호(선택) → 등록. 선생님당 1행(중복이면 서버 409)
const WiredPhoneSettingModal : React.FC<any> = (props) => {

    //선생님은 인원이 많아 Select 대신 Autocomplete(타이핑 검색). 선택값은 {id, name, label}
    const [selectedTeacher, setSelectedTeacher] = useState<any>(null);
    const [agentExtension, setAgentExtension] = useState<string>("");
    const [branchQueue, setBranchQueue] = useState<string>(NONE);
    const [submitting, setSubmitting] = useState<boolean>(false);

    //열릴 때마다 선택값 초기화
    useEffect(() => {
        if (!props.open) {
            return;
        }
        setSelectedTeacher(null);
        setAgentExtension("");
        setBranchQueue(NONE);
    }, [props.open]);

    const teacherOptions = (props.teachers || []).map((t : any) => ({
        id: t.id,
        name: t.name,
        label: `${t.name} <${t.location || "미지정"}>`
    }));

    const submit = async () => {
        if (!selectedTeacher) {
            toast.error("선생님을 선택해주세요", toastOptions);
            return;
        }
        if (!agentExtension) {
            toast.error("상담사 번호를 선택해주세요", toastOptions);
            return;
        }
        setSubmitting(true);
        try {
            const result : any = await postFetch("/dashboard/callcenter/outcall/defaults/add", {
                userId: selectedTeacher.id,
                agentExtension,
                branchQueue: branchQueue === NONE ? null : branchQueue
            });
            if (result.message === "success") {
                toast.success("등록되었습니다", toastOptions);
                props.onAdded();
                props.onClose();
            } else {
                toast.error(errorText(result.message), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        }
        setSubmitting(false);
    };

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalDialog>
                <div style={{ width: "360px" }}>
                    <div className={styles.modalTitle}>유선폰 설정하기</div>

                    <div className={styles.modalLabel}>선생님</div>
                    <Autocomplete
                        options={teacherOptions}
                        value={selectedTeacher}
                        onChange={(_ : any, newValue : any) => setSelectedTeacher(newValue)}
                        isOptionEqualToValue={(option : any, value : any) => option.id === value.id}
                        size="small"
                        noOptionsText="검색 결과 없음"
                        renderInput={(params : any) => <TextField {...params} placeholder="이름을 입력해서 검색" />}
                    />

                    <div className={styles.modalLabel}>상담사 번호</div>
                    <Select
                        placeholder="내선 선택"
                        value={agentExtension || null}
                        onChange={(e : any, v : any) => { if (v !== null) setAgentExtension(v); }}
                    >
                        {
                            (props.agents || []).map((a : any) => (
                                <Option key={a.extension} value={a.extension}>{a.name} ({a.extension})</Option>
                            ))
                        }
                    </Select>

                    <div className={styles.modalLabel}>발신번호 <span style={{ fontWeight: 400, color: "#888" }}>(선택)</span></div>
                    <Select
                        value={branchQueue}
                        onChange={(e : any, v : any) => { if (v !== null) setBranchQueue(v); }}
                    >
                        <Option value={NONE}>선택 안 함</Option>
                        {
                            (props.branches || []).map((b : any) => (
                                <Option key={b.queue} value={b.queue}>{b.name} ({b.displayNumber})</Option>
                            ))
                        }
                    </Select>
                    <div className={styles.modalHint}>전화걸기 패널을 열면 여기서 설정한 값이 기본으로 선택됩니다. 변경은 삭제 후 다시 등록하세요.</div>

                    <div className={styles.modalButtons}>
                        <Button variant="plain" color="neutral" onClick={props.onClose} disabled={submitting}>취소</Button>
                        <Button variant="solid" onClick={submit} loading={submitting}>등록</Button>
                    </div>
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default WiredPhoneSettingModal;
